import { ContentCopyOutlined, DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { DashedAddButton } from "../../../components/UIToolkit/Clickable/DashedAddButton";
import { GlyphButton } from "../../../components/UIToolkit/Clickable/GlyphButton";
import { type CustomPrintoutSummary, MAX_CUSTOM_PRINTOUTS } from "./customPrintoutTypes";

type Props = {
	/** Can design, rename and delete: New, Rename and Delete are shown. */
	canEdit: boolean;
	/** The chosen saved printout's version history control; `null` while a new printout is shown. Everyone sees it. */
	versionHistory: ReactNode;
	printoutCount: number;
	/** The chosen saved printout, which Rename and Delete act on; `undefined` while a new one is shown. */
	selected: CustomPrintoutSummary | undefined;
	/** A fresh, untouched new printout is already shown, so New has nothing to start. */
	isOnFreshNew: boolean;
	/** Annie is designing: nothing changes under her. */
	locked: boolean;
	onNew: () => void;
	/** Starts a new printout from `selected`'s current template. */
	onNewFromSelected: () => void;
	onRename: () => void;
	onDelete: () => void;
};

/**
 * The row under the custom printout list: New, then the chosen printout's version history, New from this one, Rename
 * and Delete. People who can't edit see only the version history.
 */
export const CustomPrintoutListActions: FC<Props> = ({
	canEdit,
	versionHistory,
	printoutCount,
	selected,
	isOnFreshNew,
	locked,
	onNew,
	onNewFromSelected,
	onRename,
	onDelete,
}) => {
	const { t } = useTranslation();
	const isAtLimit = printoutCount >= MAX_CUSTOM_PRINTOUTS;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			{canEdit && (
				<InHouseTooltip
					title={t(isAtLimit ? "printout.custom.ai.new.limit" : "printout.custom.ai.new.tooltip", { max: MAX_CUSTOM_PRINTOUTS })}
					placement="bottom-start"
				>
					{/* A wrapper the tooltip can hold a ref to — and that still hears the hover when the button is disabled. */}
					<Box component="span" sx={{ display: "inline-flex" }}>
						<DashedAddButton
							label={t("printout.custom.ai.new")}
							onClick={onNew}
							disabled={locked || isAtLimit || isOnFreshNew}
							dataTestId="custom-printout-new-button"
						/>
					</Box>
				</InHouseTooltip>
			)}
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.25, marginInlineStart: "auto" }}>
				{versionHistory !== null && <Box sx={{ display: "inline-flex", marginInlineEnd: 0.5 }}>{versionHistory}</Box>}
				{/* Hidden rather than disabled while Annie works: `GlyphButton` has no disabled state. The version history
				    stays: opening an earlier version asks first, and stops her. */}
				{canEdit && selected !== undefined && !locked && (
					<>
						{/* Hidden at the limit, where New's tooltip already says why no printout can be added. */}
						{!isAtLimit && (
							<InHouseTooltip title={t("printout.custom.ai.new.from", { name: selected.name })} placement="top">
								<GlyphButton
									icon={<ContentCopyOutlined />}
									ariaLabel={t("printout.custom.ai.new.from", { name: selected.name })}
									onClick={onNewFromSelected}
									dataTestId="custom-printout-new-from-button"
								/>
							</InHouseTooltip>
						)}
						<InHouseTooltip title={t("printout.custom.ai.rename", { name: selected.name })} placement="top">
							<GlyphButton
								icon={<EditOutlined />}
								ariaLabel={t("printout.custom.ai.rename", { name: selected.name })}
								onClick={onRename}
								dataTestId="custom-printout-rename-button"
							/>
						</InHouseTooltip>
						<InHouseTooltip title={t("printout.custom.ai.delete", { name: selected.name })} placement="top">
							<GlyphButton
								icon={<DeleteOutlined />}
								ariaLabel={t("printout.custom.ai.delete", { name: selected.name })}
								onClick={onDelete}
								dataTestId="custom-printout-delete-button"
							/>
						</InHouseTooltip>
					</>
				)}
			</Box>
		</Box>
	);
};
